import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const STATUSES = ["new", "contacted", "screened", "scheduled", "closed"];
const PLATFORMS = ["WhatsApp video", "Google Meet", "Zoom"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("new");
  // Booking-confirm state
  const [confirmSlot, setConfirmSlot] = useState("");
  const [confirmPlatform, setConfirmPlatform] = useState("WhatsApp video");
  const [confirmLink, setConfirmLink] = useState("");
  const [confirmNote, setConfirmNote] = useState("");
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? leads : leads.filter((l) => l.source === filter);

  const save = async () => {
    if (!open) return;
    try {
      await api.patch(`/admin/leads/${open.id}`, { status, notes });
      toast.success("Lead updated");
      setOpen(null);
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const confirmSlotAction = async () => {
    if (!open || !confirmSlot) return;
    setConfirming(true);
    try {
      await api.post(`/admin/leads/${open.id}/confirm-slot`, {
        slot: confirmSlot, platform: confirmPlatform, join_link: confirmLink || null, note: confirmNote || null,
      });
      toast.success("Slot confirmed and email sent");
      setOpen(null);
      load();
    } catch (e) {
      toast.error("Confirm failed");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex items-center justify-between">
        <div>
          <div className="kbd">— Leads</div>
          <h1 className="mt-2 font-serif text-4xl tracking-tightest">Enquiries</h1>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="leads-filter" className="w-48"><SelectValue/></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All sources</SelectItem>
      <SelectItem value="contact">Contact form</SelectItem>
      <SelectItem value="urgent">Urgent</SelectItem>
      <SelectItem value="nri">NRI</SelectItem>
      <SelectItem value="booking">Booking</SelectItem>
      <SelectItem value="second-opinion">Second opinion</SelectItem>
      <SelectItem value="reserve">Reserve call-back</SelectItem>
      <SelectItem value="book-abandoned">Booking abandoned</SelectItem>
    </SelectContent>
        </Select>
      </div>

      <div className="mt-8 hairline bg-white/70 divide-y divide-black/10">
        {loading && <div className="p-6 kbd">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="p-6 text-ink-muted">No enquiries.</div>}
        {filtered.map((l) => (
          <button
            key={l.id}
            data-testid={`lead-row-${l.id}`}
            onClick={() => { setOpen(l); setStatus(l.status || "new"); setNotes(l.notes || ""); }}
            className="w-full text-left p-4 md:p-5 grid grid-cols-12 gap-4 items-center hover:bg-black/[0.02] transition-colors"
          >
            <div className="col-span-6 md:col-span-3">
              <div className="text-sm font-medium">{l.name}</div>
              <div className="kbd">{l.email}</div>
            </div>
            <div className="col-span-6 md:col-span-2 text-sm">{l.phone}</div>
            <div className="col-span-6 md:col-span-2 kbd">{l.country}</div>
            <div className="col-span-6 md:col-span-2">
              <Badge variant="outline" className={
                l.source === "urgent" ? "border-crimson text-crimson" :
                l.source === "nri" ? "border-forest text-forest" :
                l.source === "booking" ? "border-ink bg-ink text-bone" : "border-ink/30"
              }>{l.source}</Badge>
            </div>
            <div className="col-span-6 md:col-span-2 kbd">{l.status || "new"}{l.step_reached != null ? ` · s${l.step_reached}` : ""}</div>
            <div className="col-span-6 md:col-span-1 kbd text-right">{(l.created_at||"").slice(5,10)}</div>
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => { if (!v) { setOpen(null); setConfirmSlot(""); setConfirmLink(""); setConfirmNote(""); } }}>
        <DialogContent data-testid="lead-detail-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tightest">{open?.name}</DialogTitle>
          </DialogHeader>
          {open && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><div className="kbd">Email</div><div>{open.email}</div></div>
                <div><div className="kbd">Phone</div><div>{open.phone}</div></div>
                <div><div className="kbd">Country</div><div>{open.country}</div></div>
                <div><div className="kbd">Source</div><div>{open.source}</div></div>
                <div><div className="kbd">Condition</div><div>{open.condition || "—"}</div></div>
                <div><div className="kbd">Created</div><div>{(open.created_at||"").slice(0,16).replace("T"," ")}</div></div>
              </div>
              {open.message && (
                <div>
                  <div className="kbd">Message</div>
                  <div className="mt-1 p-3 hairline bg-bone/60">{open.message}</div>
                </div>
              )}
              {open.source === "booking" && (
                <div data-testid="confirm-slot-block" className="p-4 hairline bg-bone/60 space-y-3">
                  <div className="kbd text-ink">Booking preferences</div>
                  <div className="text-sm grid grid-cols-2 gap-2 text-ink-muted">
                    <div><span className="kbd">Day</span><div className="text-ink">{open.preferred_day || "—"}</div></div>
                    <div><span className="kbd">Time</span><div className="text-ink">{open.preferred_time || "—"}</div></div>
                    <div><span className="kbd">Platform</span><div className="text-ink">{open.platform || "—"}</div></div>
                    <div><span className="kbd">Urgency</span><div className="text-ink">{open.urgency || "—"}</div></div>
                  </div>
                  {open.booking_slot && (
                    <div className="pt-3 border-t border-black/10">
                      <div className="kbd text-forest">Confirmed slot</div>
                      <div className="mt-1 text-sm text-ink">{open.booking_slot} · {open.booking_platform}</div>
                      {open.booking_join_link && <div className="mt-1 text-xs break-all"><a href={open.booking_join_link} target="_blank" rel="noreferrer" className="text-crimson underline">{open.booking_join_link}</a></div>}
                    </div>
                  )}
                  <div className="pt-3 border-t border-black/10">
                    <div className="kbd">Confirm a slot & email patient</div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Input data-testid="confirm-slot-input" placeholder="e.g., Tue 10 Dec, 15:00 IST" value={confirmSlot} onChange={(e)=>setConfirmSlot(e.target.value)}/>
                      <Select value={confirmPlatform} onValueChange={setConfirmPlatform}>
                        <SelectTrigger data-testid="confirm-platform-select"><SelectValue/></SelectTrigger>
                        <SelectContent>{PLATFORMS.map((p)=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input data-testid="confirm-link-input" placeholder="Join link (optional)" value={confirmLink} onChange={(e)=>setConfirmLink(e.target.value)} className="mt-2"/>
                    <Textarea data-testid="confirm-note-input" placeholder="Note to patient (optional)" value={confirmNote} onChange={(e)=>setConfirmNote(e.target.value)} className="mt-2" rows={2}/>
                    <button type="button" data-testid="confirm-slot-btn" onClick={confirmSlotAction} disabled={confirming || !confirmSlot} className="btn-pill-inv mt-3 disabled:opacity-60">
                      {confirming ? "Confirming…" : "Confirm & email patient"}
                    </button>
                  </div>
                </div>
              )}
              <div>
                <div className="kbd">Status</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="lead-status-select" className="mt-2"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="kbd">Internal notes</div>
                <Textarea data-testid="lead-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="mt-2"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(null)} className="btn-pill">Cancel</button>
            <button onClick={save} data-testid="lead-save-btn" className="btn-pill-inv">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
